/**
 * v2 API contract of «Проекты» (/projects, /projects/:id; docs/research/design2-records.md §3, §8
 * R1, R5, R6, R11, R12; decisions-log V9–V11, V32): the list, the card with its client links,
 * billing form, prices and invoice history, and the inline writes.
 */
import { z } from 'zod';
import { WORK_CALENDARS } from '../calendar';
import type { WorkCalendarCode } from '../calendar';
import { isIsoDate } from '../dates';
import type { BillingMode } from '../calc/prices';
import type { ResolvedPayView } from './people';

export type ProjectCardStatus = 'active' | 'paused' | 'ended';

/** Billing forms offered by the card's «Форма договора» dropdown (V50; payroll added in V54). */
export const PROJECT_CONTRACT_FORMS = ['hourly', 'fixed', 'scope', 'payroll'] as const;
export type ProjectContractForm = (typeof PROJECT_CONTRACT_FORMS)[number];

/** A record picked in a select. */
export interface CardOption {
  readonly id: string;
  readonly name: string;
}

/** Client price from a date: `rateE4` for hourly, `amountMinor` for fixed, payroll and scope. */
export interface ProjectPriceView {
  readonly effectiveFrom: string;
  readonly rateE4: number | null;
  readonly amountMinor: number | null;
  readonly currency: string;
}

export interface ProjectListRow {
  readonly id: string;
  readonly name: string;
  readonly billing: BillingMode;
  readonly status: ProjectCardStatus;
  readonly issuerId: string | null;
  /** «Тип»: the project's business lines from Settings → «Направления», by name. */
  readonly typeNames: readonly string[];
  /** Client paying for the current month (filter haystack). */
  readonly payerName: string | null;
  /** Price in force for the current month (R1); null = «нет ставки». */
  readonly price: ProjectPriceView | null;
  /**
   * «В месяц, USD» (V134, V136): the average billed per invoiced month — Σ lines of sent invoices in
   * USD at each invoice's date / the months they bill; a fixed project's fee at today's rate; null
   * when nothing is billed yet.
   */
  readonly monthlyUsdMinor: number | null;
  /**
   * «Выплачено, USD» (V136): what the paid payouts paid for the project — each payout's paid amount
   * split over its projects by their lines — in USD at the rate of the payout month's end.
   */
  readonly paidOutUsdMinor: number;
  /** Invoice lines and payouts left out of the two sums for want of a rate. */
  readonly unconverted: number;
}

export interface ProjectListResponse {
  readonly rows: readonly ProjectListRow[];
}

export const projectListQuerySchemaV2 = z.object({
  issuer: z.string().min(1).default('all'),
});
export type ProjectListQueryV2 = z.infer<typeof projectListQuerySchemaV2>;

/** `main` = the project's client, `extra` = another counterparty, `payer` = payer of some months. */
export type ProjectClientRole = 'main' | 'extra' | 'payer';

export interface ProjectClientLinkView {
  readonly clientId: string;
  readonly name: string;
  readonly contractId: string | null;
  readonly contractNumber: string | null;
  readonly role: ProjectClientRole;
}

/** Colour rule of the «Дней» cell (R6). */
export type ProjectInvoiceDaysTone = 'late' | 'onTime' | 'overdue' | 'waiting';

export type ProjectInvoiceStatus = 'issued' | 'sent' | 'partially_paid' | 'paid' | 'cancelled';

/** One row of «Счета и оплаты» (R6): an invoice that bills the record. */
export interface ProjectInvoiceRow {
  readonly invoiceId: string;
  /** null = a payroll statement («Ведомость»). */
  readonly number: string | null;
  /** Second line: the payer (project card) or the project (client card). */
  readonly subtitle: string;
  readonly period: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly status: ProjectInvoiceStatus;
  readonly sentOn: string | null;
  readonly dueOn: string | null;
  readonly paidOn: string | null;
  /** Sent → paid, or sent → today while unpaid; null = «—». */
  readonly days: number | null;
  readonly daysTone: ProjectInvoiceDaysTone | null;
  /** Days past the due date (paid late or overdue), for the cell title. */
  readonly lateDays: number;
}

export type ProjectEventKind = 'start' | 'paused' | 'ended';

/** «Прочие события» of the history tab. */
export interface ProjectEventView {
  readonly kind: ProjectEventKind;
  readonly date: string;
  /** Start: the main client name (null → «Без клиента»). */
  readonly clientName: string | null;
}

/** «Команда» of the project card: one row per assignment, active first. */
export interface ProjectTeamRow {
  readonly assignmentId: string;
  readonly personId: string;
  readonly name: string;
  readonly role: string | null;
  readonly from: string | null;
  readonly to: string | null;
  readonly isEnded: boolean;
  /** Pay in force (own or from the person's terms); null = no rate. */
  readonly pay: ResolvedPayView | null;
}

export interface ProjectCard {
  readonly id: string;
  readonly name: string;
  readonly status: ProjectCardStatus;
  readonly billing: BillingMode;
  readonly needsInvoice: boolean;
  /** «Рабочий календарь» (V305): whose holidays the project's workdays skip. */
  readonly workCalendar: WorkCalendarCode;
  /** «Позиций» of an hourly project (V309): its plan is this many × 8 h per workday; null = the team's plans. */
  readonly positions: number | null;
  readonly clientId: string | null;
  readonly contractId: string | null;
  readonly issuerId: string | null;
  /** «Тип»: the project's business lines. */
  readonly businessLineIds: readonly string[];
  /** Live business lines plus the chosen ones even when archived. */
  readonly businessLineOptions: readonly CardOption[];
  readonly startOn: string | null;
  readonly endOn: string | null;
  /** Price in force for the current month (R1). */
  readonly price: ProjectPriceView | null;
  /** Currency of a new inline price: the price's, else the payer's, else USD. */
  readonly priceCurrency: string;
  /** Every price, oldest first. */
  readonly prices: readonly ProjectPriceView[];
  readonly clients: readonly ProjectClientLinkView[];
  /** Live, non-archived clients not linked yet («+ Добавить клиента»). */
  readonly clientOptions: readonly CardOption[];
  readonly invoices: readonly ProjectInvoiceRow[];
  readonly events: readonly ProjectEventView[];
  readonly team: readonly ProjectTeamRow[];
  readonly today: string;
  /** 1st of next month: default date of capture presets. */
  readonly nextMonthStart: string;
}

/** Result of every inline write: the activity / toast line and its undo token. */
export interface CardWriteResult {
  readonly message: string;
  /** Null when the value did not change (nothing written). */
  readonly undoToken: string | null;
}

const positiveInt = z.number().int().positive();
const nullableId = z.string().min(1).nullable();
const nullableDate = z.string().refine(isIsoDate, 'Дата в формате ГГГГ-ММ-ДД').nullable();

/** Client price effective today (R12): the value the billing form reads. */
export const projectPriceTodaySchema = z
  .object({ rateE4: positiveInt.optional(), amountMinor: positiveInt.optional() })
  .refine((body) => (body.rateE4 == null) !== (body.amountMinor == null), 'Укажите ставку или сумму');
export type ProjectPriceTodayBody = z.infer<typeof projectPriceTodaySchema>;

const PROJECT_TITLE_MAX = 200;

const PROJECT_PATCH_FIELDS = ['title', 'billingMode', 'needsInvoice', 'workCalendar', 'positions', 'issuerId', 'clientId', 'contractId', 'startOn', 'endOn'] as const;
const POSITIONS_MAX = 999;

/** One field per request (inline field pattern). */
export const projectPatchSchemaV2 = z
  .object({
    title: z.string().trim().min(1, 'Укажите название').max(PROJECT_TITLE_MAX).optional(),
    billingMode: z.enum(PROJECT_CONTRACT_FORMS).optional(),
    needsInvoice: z.boolean().optional(),
    workCalendar: z.enum(WORK_CALENDARS).optional(),
    positions: z.number().int().min(1, 'Позиций — число от 1').max(POSITIONS_MAX, `Не больше ${POSITIONS_MAX}`).nullable().optional(),
    issuerId: nullableId.optional(),
    clientId: z.string().min(1).optional(),
    contractId: nullableId.optional(),
    startOn: nullableDate.optional(),
    endOn: nullableDate.optional(),
  })
  .strict()
  .refine((body) => PROJECT_PATCH_FIELDS.filter((key) => body[key] !== undefined).length === 1, 'Измените одно поле');
export type ProjectPatchBodyV2 = z.infer<typeof projectPatchSchemaV2>;

/** «Тип»: the whole set of the project's business lines (multiselect). */
export const projectTypesSchema = z.object({ businessLineIds: z.array(z.string().min(1)).max(50) });
export type ProjectTypesBody = z.infer<typeof projectTypesSchema>;

export const projectClientAddSchema = z.object({ clientId: z.string().min(1) });
export type ProjectClientAddBody = z.infer<typeof projectClientAddSchema>;
